import { useState, useEffect, useCallback } from 'react'
import cutileiApi from 'services/cutileiApi'
import PayoutService from 'services/Payout'
import { logApiError } from 'services/apiError'
import { groupByDate, formatGroupDate } from '../dateGroups'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import Select from 'components/Inputs/Select'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaHandHoldingUsd, FaRegClock, FaBolt } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Content,
  Card,
  CardHeader,
  CardTitle,
  HeroCard,
  HeroLabel,
  HeroBalance,
  HeroFooter,
  HeroDetail,
  DataList,
  DataRow,
  DataLabel,
  TransactionList,
  TransactionRow,
  TransactionIcon,
  TransactionInfo,
  TransactionMeta,
  TransactionSide,
  DateGroupLabel,
  StatusBadge,
  EmptyState,
  PlainMoneyText,
  MoneyTextMask,
  Row,
  FormField,
  WarningText,
  Caption,
  InfoText
} from '../styles'

const PER_PAGE = 20

/*
  Recebíveis: o que a Cutilei deve ao titular pelos atendimentos de membro já validados, e a
  opção de receber antes do vencimento pagando uma taxa.

  O recebível nasce quando o salão valida o atendimento e é pago sozinho trinta dias depois da
  data em que ele aconteceu — não há fechamento mensal, e é por isso que a lista é agrupada
  pela data do ATENDIMENTO e não pela data do crédito: é assim que o salão pensa o próprio
  faturamento.

  Antecipar não cria um estado novo nem adianta o vencimento contratual: ele move só a data em
  que a máquina paga. É o que permite mostrar, depois, quantos dias a taxa comprou.
*/
export default function Payouts ({ requestConfig }) {
  const [loading, setLoading] = useState (true)
  const [submitting, setSubmitting] = useState (false)
  const [loadingMore, setLoadingMore] = useState (false)
  const [payouts, setPayouts] = useState ([])
  const [meta, setMeta] = useState (null)
  const [summary, setSummary] = useState (null)
  const [anticipation, setAnticipation] = useState (null)
  const [status, setStatus] = useState (PayoutService.STATUS_FILTERS[0])
  const [error, setError] = useState (null)
  const [notice, setNotice] = useState (null)
  /* A cotação a confirmar. `{ message, onConfirm }` enquanto o diálogo está aberto. */
  const [confirmation, setConfirmation] = useState (null)

  const canAnticipate = PayoutService.canAnticipate ()

  const getPayouts = useCallback (async (page = 1, filter = status?.value) => {
    const query = new URLSearchParams ({
      page,
      per_page: PER_PAGE,
      ...PayoutService.getOwnerParams ()
    })

    if (filter) query.set ('status', filter)

    const { data } = await cutileiApi.get (
      `/payouts?${query.toString ()}`, requestConfig
    )

    return data
  }, [status, requestConfig])

  /*
    O resumo do titular e o da antecipação são duas consultas porque respondem a perguntas
    diferentes: um diz quanto ainda vem, o outro quanto disso pode vir agora e a que custo. O
    segundo não existe para administrador — as rotas de antecipação recusam `adminAuth` de
    propósito —, então nem é pedido.
  */
  const load = useCallback (async filter => {
    try {
      setLoading (true)

      const [summaryData, payoutsData, anticipationData] = await Promise.all ([
        cutileiApi.get (
          `/payouts/summary?${new URLSearchParams (PayoutService.getOwnerParams ())}`,
          requestConfig
        ),
        getPayouts (1, filter),
        canAnticipate
          ? cutileiApi.get ('/payouts/anticipations/summary', requestConfig)
          : Promise.resolve ({ data: null })
      ])

      setSummary (summaryData.data)
      setAnticipation (anticipationData.data)
      setPayouts (PayoutService.normalizeList (payoutsData.data ?? []))
      setMeta (payoutsData.meta ?? null)
      setError (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar seus recebíveis')
    } finally {
      setLoading (false)
    }
  }, [getPayouts, canAnticipate, requestConfig])

  useEffect (() => {
    load (PayoutService.STATUS_FILTERS[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterChange = async (_name, selected) => {
    setStatus (selected)
    await load (selected?.value)
  }

  const loadMore = async () => {
    if (!meta || meta.current_page >= meta.last_page) return

    try {
      setLoadingMore (true)
      const data = await getPayouts (meta.current_page + 1)

      setPayouts (prev => [...prev, ...PayoutService.normalizeList (data.data ?? [])])
      setMeta (data.meta ?? null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar mais recebíveis')
    } finally {
      setLoadingMore (false)
    }
  }

  /*
    Antecipa um recebível só. A cotação vem da API antes de qualquer confirmação: a taxa é
    congelada no pedido, então o titular precisa ver o líquido exato que vai receber — e não
    uma conta que a tela fez com um percentual que pode ter mudado desde a última leitura.
  */
  const confirmOne = async payout => {
    try {
      setSubmitting (true)
      setError (null)
      setNotice (null)

      const { data: quote } = await cutileiApi.get (
        `/payouts/${payout.id}/anticipation`, requestConfig
      )

      setConfirmation ({
        message: PayoutService.getAnticipationMessage (quote),
        onConfirm: () => anticipateOne (payout)
      })
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível calcular a antecipação')
    } finally {
      setSubmitting (false)
    }
  }

  /*
    Um segundo toque devolve 200 com `duplicated: true` e o mesmo recebível — é sucesso, e não
    erro: quem tocou duas vezes não viu a primeira resposta, e a taxa foi cobrada uma vez só.
    Tratá-lo como falha faria o titular acreditar que a antecipação não saiu.
  */
  const anticipateOne = async payout => {
    try {
      setSubmitting (true)
      setError (null)

      const { data } = await cutileiApi.post (
        `/payouts/${payout.id}/anticipation`, {}, requestConfig
      )

      setNotice (data.duplicated
        ? 'Este recebível já havia sido antecipado. A taxa foi cobrada uma vez só'
        : 'Antecipação concluída! O valor entra no próximo pagamento'
      )

      await load ()
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível antecipar este recebível')
    } finally {
      setSubmitting (false)
    }
  }

  /*
    O lote não precisa de cotação: os números do cartão já vêm de
    `/payouts/anticipations/summary`, que soma exatamente os elegíveis.
  */
  const confirmAll = () => {
    setError (null)
    setNotice (null)
    setConfirmation ({
      message: PayoutService.getAnticipationMessage ({
        ...anticipation,
        count: anticipation.count
      }),
      onConfirm: anticipateAll
    })
  }

  /*
    A resposta traz `skipped` com o motivo de cada recusa. Um recebível que venceu ou entrou em
    pagamento entre a tela e o toque é esperado numa lista grande — dizer quantos não entraram
    é honesto, e fingir que tudo foi antecipado deixaria o titular achando que a lista está
    errada quando ela reaparecer.
  */
  const anticipateAll = async () => {
    try {
      setSubmitting (true)
      setError (null)

      const { data } = await cutileiApi.post ('/payouts/anticipations', {}, requestConfig)
      const anticipated = data.anticipated?.length ?? 0
      const skipped = data.skipped?.length ?? 0

      if (anticipated === 0) {
        setNotice ('Nenhum recebível pôde ser antecipado agora')
      } else {
        setNotice (
          `${anticipated} ${anticipated === 1 ? 'recebível' : 'recebíveis'} antecipados, `
          + `${PayoutService.formatMoney (data.netTotal)} no próximo pagamento`
          + (skipped > 0 ? `. ${skipped} não puderam ser antecipados` : '')
        )
      }

      await load ()
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível antecipar seus recebíveis')
    } finally {
      setSubmitting (false)
    }
  }

  if (loading) return <Loading style={{alignSelf: 'center', margin: '40px auto'}}/>

  const nextPayableAt = summary?.nextPayableAt
    ? DateTime.fromISO (summary.nextPayableAt).setLocale ('pt-BR')
    : null

  const groups = groupByDate (
    payouts,
    payout => DateTime.fromISO (payout.scheduleDate)
  )

  const hasMore = meta && meta.current_page < meta.last_page
  const showAnticipation = canAnticipate && (anticipation?.count ?? 0) > 0

  return (
    <Container>
      <Content>
        <HeroCard>
          <HeroLabel>A receber da Cutilei</HeroLabel>
          <HeroBalance value={Number (summary?.pendingTotal ?? 0)}/>
          <HeroFooter>
            <HeroDetail>
              <span>Atendimentos</span>
              <b>{summary?.pendingCount ?? 0}</b>
            </HeroDetail>
            {nextPayableAt?.isValid && (
              <HeroDetail>
                <span>Próximo crédito</span>
                <b>{nextPayableAt.toFormat ('dd/MM')}</b>
              </HeroDetail>
            )}
          </HeroFooter>
        </HeroCard>

        {error && <WarningText>{error}</WarningText>}

        {/* Não é erro: é o desfecho de uma antecipação que acabou de acontecer. */}
        {!error && notice && <Caption>{notice}</Caption>}

        {showAnticipation && (
          <Card gap={12}>
            <CardHeader>
              <CardTitle>Antecipar recebimentos</CardTitle>
              <StatusBadge color='#FF9800'>
                <FaBolt size={10}/>
                {`${anticipation.count} ${anticipation.count === 1 ? 'disponível' : 'disponíveis'}`}
              </StatusBadge>
            </CardHeader>
            <DataList>
              <DataRow>
                <DataLabel>Valor disponível</DataLabel>
                <MoneyTextMask value={Number (anticipation.amount ?? 0)}/>
              </DataRow>
              <DataRow>
                <DataLabel>{`Taxa de antecipação (${Number (anticipation.feePercentage ?? 0)}%)`}</DataLabel>
                <MoneyTextMask value={Number (anticipation.feeAmount ?? 0)}/>
              </DataRow>
              <DataRow>
                <DataLabel>Você recebe</DataLabel>
                <PlainMoneyText value={Number (anticipation.netAmount ?? 0)}/>
              </DataRow>
            </DataList>
            <MainButton
              label='Antecipar tudo'
              onClick={confirmAll}
              disabled={submitting}
              loading={submitting}
              width={170}
              style={{alignSelf: 'center'}}
            />
            <InfoText align='center'>
              {'Recebíveis que já venceram não entram: eles são pagos no próximo '
              + 'processamento de qualquer forma, sem taxa'}
            </InfoText>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Meus recebíveis</CardTitle>
          </CardHeader>
          <Row>
            <FormField stretch={2}>
              <Select
                name='status'
                placeholder='Filtrar recebíveis'
                options={PayoutService.STATUS_FILTERS}
                value={status}
                onChange={handleFilterChange}
                onBlur={() => {}}
              />
            </FormField>
          </Row>
          {payouts.length === 0 ? (
            <EmptyState>
              <FaHandHoldingUsd size={26}/>
              <b>Nenhum recebível por aqui</b>
              <p>
                {status?.value
                  ? 'Nenhum recebível nesta situação'
                  : 'Os atendimentos de membros da Cutilei que você validar vão aparecer '
                    + 'nesta lista, e são pagos 30 dias depois do atendimento'}
              </p>
            </EmptyState>
          ) : (
            <TransactionList>
              {groups.map (group => (
                <div key={group.key}>
                  <DateGroupLabel>{formatGroupDate (group.date)}</DateGroupLabel>
                  {group.items.map (payout => (
                    <TransactionRow key={payout.id}>
                      <TransactionIcon credit={payout.status === 'paid'}>
                        <FaHandHoldingUsd size={16}/>
                      </TransactionIcon>
                      <TransactionInfo>
                        <b>{payout.schedule?.service?.name ?? 'Atendimento Cutilei'}</b>
                        <TransactionMeta>
                          <span>{PayoutService.getUserTypeLabel (payout.userType)}</span>
                          {/*
                            O vencimento só aparece no que ainda não foi pago: numa lista de
                            recebíveis quitados ele é a data de um compromisso que já passou.
                          */}
                          {payout.status === 'pending' && payout.dueAt && (
                            <span>
                              {`Vence ${DateTime.fromISO (payout.dueAt).toFormat ('dd/MM')}`}
                            </span>
                          )}
                          {payout.anticipated && <span>Antecipado</span>}
                        </TransactionMeta>
                      </TransactionInfo>
                      <TransactionSide>
                        <PlainMoneyText value={payout.netAmount}/>
                        {/*
                          A situação só aparece quando não é a esperada — como no extrato:
                          um selo em cada linha de uma lista onde quase tudo está no esperado
                          é ruído.
                        */}
                        {payout.status !== 'pending' && (
                          <StatusBadge color={PayoutService.getStatusColor (payout.status)}>
                            {PayoutService.getStatusLabel (payout.status)}
                          </StatusBadge>
                        )}
                        {canAnticipate && payout.anticipable && (
                          <SecondaryButton
                            label='Antecipar'
                            onClick={() => confirmOne (payout)}
                            disabled={submitting}
                            width={110}
                            height={30}
                          />
                        )}
                      </TransactionSide>
                    </TransactionRow>
                  ))}
                </div>
              ))}
            </TransactionList>
          )}
          {hasMore && (
            <SecondaryButton
              label='Carregar mais'
              loading={loadingMore}
              onClick={loadMore}
              color='#606060'
              width={160}
              height={34}
              style={{alignSelf: 'center'}}
            />
          )}
          {payouts.length > 0 && meta && (
            <InfoText align='center'>
              {`${payouts.length} de ${meta.total} `
              + `${meta.total === 1 ? 'recebível' : 'recebíveis'}`}
            </InfoText>
          )}
          {summary?.paidCount > 0 && (
            <InfoText align='center'>
              <FaRegClock size={11} style={{marginRight: 6, marginBottom: -1}}/>
              {`Você já recebeu ${PayoutService.formatMoney (summary.paidTotal)} da Cutilei `
              + `em ${summary.paidCount} ${summary.paidCount === 1 ? 'atendimento' : 'atendimentos'}`}
            </InfoText>
          )}
        </Card>
      </Content>

      <AlertDialog
        visible={Boolean (confirmation)}
        title='Confirmar antecipação'
        message={confirmation?.message ?? ''}
        confirmText='Confirmar'
        cancelText='Cancelar'
        containerStyle={{whiteSpace: 'pre-line'}}
        onConfirm={() => confirmation?.onConfirm ()}
        onClose={() => setConfirmation (null)}
      />
    </Container>
  )
}
